import { motion } from 'framer-motion';
import { Tent, Clock, ShieldCheck, Car, Users, AlertCircle } from 'lucide-react';

export default function GateCamping() {
  return (
    <div className="min-h-screen bg-ff-blue pt-20" data-testid="gate-camping-page">
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <span className="badge mb-4">
              Event Details
            </span>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl text-ff-brown">
              Gate & <span className="text-ff-brown">Camping</span>
            </h1>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* Gate Times */}
            <div className="bg-white/40 backdrop-blur-sm rounded-3xl p-8 mb-8">
              <div className="flex items-center gap-4 mb-6">
                <Clock className="w-8 h-8 text-ff-brown" />
                <h2 className="font-display text-2xl text-ff-brown">Gate Times</h2>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-ff-outline/20">
                  <span className="text-ff-brown font-medium">Thursday 24th September</span>
                  <span className="text-ff-brown">Gates Open 10:00 AM</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-ff-outline/20">
                  <span className="text-ff-brown font-medium">Friday – Sunday</span>
                  <span className="text-ff-brown/70">Gates open from 8:30 AM</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-ff-outline/20">
                  <span className="text-ff-brown font-medium">Monday 28th September</span>
                  <span className="text-ff-brown">Checkout by 12:00 PM</span>
                </div>
              </div>
            </div>

            {/* Entry Process */}
            <div className="bg-white/40 backdrop-blur-sm rounded-3xl p-8 mb-8">
              <div className="flex items-center gap-4 mb-6">
                <ShieldCheck className="w-8 h-8 text-ff-brown" />
                <h2 className="font-display text-2xl text-ff-brown">Entry Process</h2>
              </div>
              <ol className="text-ff-brown/70 space-y-3">
                <li className="flex gap-3">
                  <span className="text-ff-brown font-bold">1.</span>
                  Have your ticket ready (digital or printed) at the gate
                </li>
                <li className="flex gap-3">
                  <span className="text-ff-brown font-bold">2.</span>
                  Present valid photo ID matching your ticket name
                </li>
                <li className="flex gap-3">
                  <span className="text-ff-brown font-bold">3.</span>
                  Vehicle and bag searches will be conducted, and entry can be refused
                </li>
                <li className="flex gap-3">
                  <span className="text-ff-brown font-bold">4.</span>
                  Receive your wristband – do not remove it!
                </li>
                <li className="flex gap-3">
                  <span className="text-ff-brown font-bold">5.</span>
                  Follow crew directions to parking and camping areas
                </li>
              </ol>
            </div>

            {/* Camping Info */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white/40 backdrop-blur-sm rounded-3xl p-6">
                <Tent className="w-6 h-6 text-ff-brown mb-4" />
                <h3 className="font-display text-xl text-ff-brown mb-3">BYO Camping</h3>
                <p className="text-ff-brown/70 text-sm">
                  This is a BYO camping festival. Bring your own tent, sleeping gear, and 
                  camping essentials. Camp near your friends – arrive together to set up together!
                </p>
              </div>
              
              <div className="bg-white/40 backdrop-blur-sm rounded-3xl p-6">
                <Car className="w-6 h-6 text-ff-brown mb-4" />
                <h3 className="font-display text-xl text-ff-brown mb-3">Car Camping</h3>
                <p className="text-ff-brown/70 text-sm">
                  Parking is available adjacent to camping areas. Once parked, vehicles 
                  cannot be moved until the end of the festival for safety reasons.
                </p>
              </div>
              
              <div className="bg-white/40 backdrop-blur-sm rounded-3xl p-6">
                <Users className="w-6 h-6 text-ff-brown mb-4" />
                <h3 className="font-display text-xl text-ff-brown mb-3">Group Camping</h3>
                <p className="text-ff-brown/70 text-sm">
                  Want to camp with your crew? Arrive together and you can set up together. 
                  Large groups should contact us in advance to arrange space.
                </p>
              </div>
              
              <div className="bg-white/40 backdrop-blur-sm rounded-3xl p-6">
                <AlertCircle className="w-6 h-6 text-ff-brown mb-4" />
                <h3 className="font-display text-xl text-ff-brown mb-3">Important Rules</h3>
                <p className="text-ff-brown/70 text-sm">
                  No open fires. No glass. Respect quiet hours in family areas. 
                  Keep your campsite tidy. Leave No Trace.
                </p>
              </div>
            </div>

            {/* Wristband Info */}
            <div className="bg-white/40 backdrop-blur-sm rounded-3xl p-8 border-l-4 border-ff-secondary">
              <h3 className="font-display text-xl text-ff-brown mb-4">Wristband Policy</h3>
              <p className="text-ff-brown/70">
                Your wristband is your ticket for the entire festival. It must remain on your 
                wrist at all times. Removed or damaged wristbands will not be replaced and you 
                may be denied re-entry. Lost wristbands will require purchase of a new ticket.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
